import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  InputGroup,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
  Label,
} from "reactstrap";
import { Formik, Form, Field, ErrorMessage, FieldArray } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { debounce } from "lodash";

import { customerReturnActions } from "../../sagas/customerReturnSlice";
import { productActions } from "../../sagas/productSlice";
import { clientActions } from "../../sagas/clientSlice";

import { formatPrice } from "../../helpers/utility";
import {
  FaBarcode,
  FaBox,
  FaBoxes,
  FaPlus,
  FaSearch,
  FaTag,
  FaTimes,
  FaTrash,
  FaUser,
  FaEraser,
} from "react-icons/fa";

import "./CustomerReturnFormPOS.css";
import POSKeypadModal from "./POSKeypadModal";
import POSNumericKeypadModal from "./POSNumericKeypadModal";
import POSHeader from "./POSHeader";
import { CBL_PAGE, CPMT_PRODUCTS_PAGE, CPMT_RECEPTION_PAGE, CPMT_STOCK_PAGE, CPMT_TRANSFER_PAGE, PAY_PAGE, RETOUR_PAGE } from "../../routes/routeConstants";
import { useLocation, useNavigate } from "react-router-dom";

const CustomerReturnForm = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const location = useLocation();
  const customerReturn = location.state?.customerReturn;

  const isEditing = Boolean(customerReturn);
  const canBeUpdated =
    (isEditing && customerReturn?.statut === "BROUILLON") || !isEditing;

  const { newReturnNumber, loading } = useSelector(
    (state) => state.customerReturns
  );
  const { clients } = useSelector((state) => state.clients);
  const { products, totalPages, totalElements, pageSize } = useSelector((s) => s.products);
  const { businessName, email, address, telephone, fullName, commercialId, pointOfSaleId } = useSelector((state) => state.auth);
  const [activeTab, setActiveTab] = useState("RETOURS");
  const unites = useSelector((s) => s.products.unites);
  const navigate = useNavigate();

  // ===== Modal Product =====
  const [chooseProductModal, setChooseProductModal] = useState(false);
  const toggleChooseProductModal = () => setChooseProductModal((v) => !v);
  const [modalSearchTerm, setModalSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isSearching, setIsSearching] = useState(false);
  const [searchKeypadOpen, setSearchKeypadOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  // ===== Modal Client =====
  const [clientModal, setClientModal] = useState(false);
  const toggleClientModal = () => setClientModal((v) => !v);
  const [clientSearch, setClientSearch] = useState("");
  const [textKeypadOpen, setTextKeypadOpen] = useState(false);
  const [textKeypadValue, setTextKeypadValue] = useState("");
  const [textFieldName, setTextFieldName] = useState(null);

  const [numberKeypadOpen, setNumberKeypadOpen] = useState(false);
  const [numberKeypadValue, setNumberKeypadValue] = useState("");
  const [numberField, setNumberField] = useState(null);

  // ===== INIT =====
  useEffect(() => {
    dispatch(clientActions.search({ query: "" }));
    dispatch(productActions.findAllUnites());
    if (!isEditing) dispatch(customerReturnActions.generateNumero());
  }, [dispatch, isEditing]);

  // Load products when modal opens or pagination changes
  useEffect(() => {
    if (!chooseProductModal) return;

    dispatch(
      productActions.findAll({
        search: modalSearchTerm || "",
        page: currentPage - 1,
        size: 10,
      })
    );
  }, [dispatch, chooseProductModal, currentPage]);

  const triggerProductSearch = (query) => {
    setSearchValue(query);
    dispatch(productActions.findAll({
      search: query,
      page: 0,
      size: 10,
    }));
  };

  useEffect(() => {
    let buffer = "";
    let timeout;

    const handleScan = (e) => {
      if (searchKeypadOpen) return;

      if (timeout) clearTimeout(timeout);

      if (e.key === "Enter") {
        if (buffer.length >= 6) { // barcode length safety
          triggerProductSearch(buffer);
        }
        buffer = "";
        return;
      }

      if (e.key.length === 1) {
        buffer += e.key;
        timeout = setTimeout(() => (buffer = ""), 100);
      }
    };

    window.addEventListener("keydown", handleScan);
    return () => window.removeEventListener("keydown", handleScan);
  }, [searchKeypadOpen]);

  // ===== HELPERS =====
  const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

  const recalcLineAmounts = (line) => {
    const qte = Number(line.quantiteRetournee) || 0;
    const prixTTC = Number(line.prixUnitaireTTC) || 0;
    const taux = Number(line.tauxTVA) || 0;

    const montantTTC = round2(qte * prixTTC);
    const montantHT = taux > 0 ? round2(montantTTC / (1 + taux)) : montantTTC;
    const montantTVA = round2(montantTTC - montantHT);

    return { ...line, montantHT, montantTVA, montantTTC };
  };

  const calculateTotals = (lines) => {
    let totalHT = 0;
    let totalTVA = 0;
    let totalTTC = 0;

    (lines || []).forEach((line) => {
      const qte = Number(line.quantiteRetournee) || 0;
      const ttc = Number(line.prixUnitaireTTC) || 0;
      const taux = Number(line.tauxTVA) || 0;

      const lineTotalTTC = round2(qte * ttc);
      const lineHT = taux > 0 ? round2(lineTotalTTC / (1 + taux)) : lineTotalTTC;
      const lineTVA = round2(lineTotalTTC - lineHT);

      totalTTC += lineTotalTTC;
      totalHT += lineHT;
      totalTVA += lineTVA;
    });

    return {
      montantHT: round2(totalHT),
      montantTVA: round2(totalTVA),
      montantTTC: round2(totalTTC),
    };
  };

  // ===== CLIENT LIST (modal) =====
  const clientList = useMemo(() => {
    const q = (clientSearch || "").trim().toLowerCase();

    const mapped =
      (clients || []).map((c) => {
        const label =
          c.typeClient === "PARTICULIER"
            ? `${c.nom || ""} ${c.prenom || ""}`.trim()
            : c.entreprise || `${c.nom || ""} ${c.prenom || ""}`.trim();

        return {
          id: c.id,
          code: c.code || "",
          label,
          raw: c,
        };
      }) || [];

    if (!q) return mapped;

    return mapped.filter((c) => {
      const str = `${c.code} ${c.label}`.toLowerCase();
      return str.includes(q);
    });
  }, [clients, clientSearch]);

  const getClientLabelById = (id) => {
    if (!id) return "-";
    const c = (clients || []).find((x) => String(x.id) === String(id));
    if (!c) return "-";
    return c.typeClient === "PARTICULIER"
      ? `${c.nom || ""} ${c.prenom || ""}`.trim()
      : c.entreprise || `${c.nom || ""} ${c.prenom || ""}`.trim();
  };

  // ===== VALIDATION =====
  const validationSchema = Yup.object().shape({
    dateOperation: Yup.date().required(
      t("customerReturns.form.validation.dateRequired")
    ),

    lignes: Yup.array().min(
      1,
      t("customerReturns.form.validation.itemsRequired")
    ),
  });

  const initialValues =
    isEditing && customerReturn
      ? {
        numeroRetour: customerReturn.numeroRetour || "",
        dateOperation:
          customerReturn.dateOperation ||
          new Date().toISOString().split("T")[0],
        clientId: customerReturn.clientId || "",
        motifRetour: customerReturn.motifRetour || "",
        modeRemboursement: customerReturn.modeRemboursement || "AVOIR",
        lignes:
          customerReturn.lignes?.map((line) => ({
            articleId: line.articleId,
            articleLibelle: line.articleNom,
            articleCode: line.articleCode,
            quantiteRetournee: line.quantiteRetournee,
            quantiteOriginale: line.quantiteOriginale,
            prixUnitaireHT: line.prixUnitaireHT,
            prixUnitaireTTC: line.prixUnitaireTTC,
            tauxTVA: line.tauxTVA,
            unite: line.unite,
          })) || [],
      }
      : {
        numeroRetour: newReturnNumber,
        dateOperation: new Date().toISOString().split("T")[0],
        clientId: "",
        motifRetour: "",
        modeRemboursement: "AVOIR",
        lignes: [],
      };

  const handleSubmit = (values, { setSubmitting }) => {
    const totals = calculateTotals(values.lignes);

    const dataToSubmit = {
      ...values,
      ...totals,
      isPassager: values.clientId ? false : true,
    };

    if (isEditing) {
      dispatch(
        customerReturnActions.update({
          id: customerReturn?.id,
          data: dataToSubmit,
          onSuccess: () => {
            Swal.fire({
              icon: "success",
              title: t("common.success"),
              text: t("customerReturns.messages.updateSuccess"),
            });
            navigate(RETOUR_PAGE);
          },
          onError: () => {
            Swal.fire({
              icon: "error",
              title: t("common.error"),
              text: t("customerReturns.messages.saveError"),
            });
            setSubmitting(false);
          },
        })
      );
    } else {
      dispatch(
        customerReturnActions.create({
          data: dataToSubmit,
          onSuccess: () => {
            Swal.fire({
              icon: "success",
              title: t("common.success"),
              text: t("customerReturns.messages.updateSuccess"),
            });
            navigate(RETOUR_PAGE);
          },
          onError: () => {
            Swal.fire({
              icon: "error",
              title: t("common.error"),
              text: t("customerReturns.messages.saveError"),
            });
            setSubmitting(false);
          },
        })
      );
    }
  };

  // ===== MODAL PRODUCT SEARCH =====
  const debouncedModalSearch = useMemo(
    () =>
      debounce((query) => {
        setIsSearching(true);
        dispatch(
          productActions.findAll({
            search: query,
            page: 0,
            size: pageSize || 10,
            onSuccess: () => setIsSearching(false),
            onError: () => setIsSearching(false),
          })
        );
      }, 300),
    [dispatch, pageSize]
  );

  const handleModalSearchChange = (e) => {
    const value = e.target.value;
    setModalSearchTerm(value);
    setCurrentPage(1);
    debouncedModalSearch(value);
  };

  if (loading && isEditing) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <Container fluid className="pos-form-page">
      <POSHeader
        businessName={businessName || "MATJARPOS"}
        commercialId={commercialId}
        pointOfSaleId={pointOfSaleId}
        fullName={fullName}
        activeTab={activeTab}
        onChangeTab={(tab) => {
          setActiveTab(tab);
          if (tab === "VENTES") navigate(CBL_PAGE);
          if (tab === "STOCK") navigate(CPMT_STOCK_PAGE);
          if (tab === "PRODUCTS") navigate(CPMT_PRODUCTS_PAGE);
          if (tab === "AVANCE") navigate(PAY_PAGE);
          if (tab === "RETOURS") navigate(RETOUR_PAGE);
          if (tab === "RECEPTION") navigate(CPMT_RECEPTION_PAGE);
          if (tab === "TRANSFER") navigate(CPMT_TRANSFER_PAGE);
        }}
      />
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, isSubmitting, setFieldValue }) => {
          const totals = calculateTotals(values.lignes);
          const lineCount = values.lignes?.length || 0;
          const canSave = canBeUpdated && lineCount > 0;

          return (
            <Form className="pos-form-root">

              {/* ===== TOP BAR ===== */}
              <Card className="pos-toolbar-card">
                <CardBody className="pos-toolbar-body">
                  <div className="pos-toolbar-left">
                    <div className="pos-title">
                      Bon Retour{" "}
                      <span className="pos-subtitle">
                        • {values.numeroRetour || "-"}
                      </span>
                    </div>
                    <div className="pos-kpis">
                      <Badge
                        color={isEditing ? "warning" : "primary"}
                        pill
                        className="pos-pill"
                      >
                        {isEditing ? "EDITION" : "NOUVEAU"}
                      </Badge>
                      <Badge color="dark" pill className="pos-pill">
                        Lignes: <span className="ms-1">{lineCount}</span>
                      </Badge>
                      <Badge color="secondary" pill className="pos-pill">
                        TTC:{" "}
                        <span className="ms-1">
                          {formatPrice(totals.montantTTC)}
                        </span>
                      </Badge>
                    </div>
                  </div>

                  <div className="pos-toolbar-right">
                    <Button
                      color="secondary"
                      className="pos-toolbar-btn"
                      onClick={() => navigate(RETOUR_PAGE)}
                    >
                      {t("common.cancel")}
                    </Button>

                    <Button
                      color="primary"
                      className="pos-toolbar-btn-primary"
                      type="submit"
                      disabled={isSubmitting || !canSave}
                    >
                      {isSubmitting ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          {t("common.loading")}
                        </>
                      ) : (
                        t("common.save")
                      )}
                    </Button>
                  </div>
                </CardBody>
              </Card>

              {/* ===== HEADER FIELDS ===== */}
              <Row className="g-2">
                <Col lg={12}>
                  <Card className="pos-card">
                    <CardBody className="pos-card-body">
                      <Row className="g-2">
                        <Col md={3}>
                          <Label className="pos-label">N° Retour</Label>
                          <Field
                            name="numeroRetour"
                            type="text"
                            className="form-control pos-input"
                            disabled
                          />
                        </Col>

                        <Col md={3}>
                          <Label className="pos-label">Date</Label>
                          <Field
                            name="dateOperation"
                            type="date"
                            className={`form-control pos-input ${errors.dateOperation && touched.dateOperation
                              ? "is-invalid"
                              : ""
                              }`}
                            disabled={!canBeUpdated}
                          />
                          <ErrorMessage
                            name="dateOperation"
                            component="div"
                            className="invalid-feedback"
                          />
                        </Col>

                        {/* ✅ CLIENT VIA MODAL */}
                        <Col md={12}>
                          <div className="d-flex justify-content-between align-items-center">
                            <Label className="pos-label mb-0">Client</Label>

                            <div className="d-flex gap-2">
                              <Button
                                type="button"
                                color="light"
                                className="pos-mini-btn"
                                onClick={() => {
                                  setFieldValue("clientId", "");
                                }}
                                disabled={!canBeUpdated}
                                title="Passager / sans client"
                              >
                                <FaEraser className="me-2" />
                                Effacer
                              </Button>

                              <Button
                                type="button"
                                color="primary"
                                className="pos-mini-btn"
                                onClick={() => {
                                  setClientSearch("");
                                  toggleClientModal();
                                }}
                                disabled={!canBeUpdated}
                              >
                                <FaUser className="me-2" />
                                Choisir client
                              </Button>
                              <Button
                                type="button"
                                color="primary"
                                onClick={toggleChooseProductModal}
                                disabled={!canBeUpdated}
                              >
                                <FaPlus className="me-2" />
                                Ajouter Article
                              </Button>
                            </div>
                          </div>

                          <div className="pos-client-box mt-2">
                            <div className="pos-client-label">
                              {values.clientId
                                ? getClientLabelById(values.clientId)
                                : "Passager / Aucun client"}
                            </div>
                            {values.clientId ? (
                              <Badge color="success" pill>
                                Client sélectionné
                              </Badge>
                            ) : (
                              <Badge color="secondary" pill>
                                Passager
                              </Badge>
                            )}
                          </div>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                </Col>                
              </Row>

              {/* ===== LINES TABLE ===== */}
              <Card className="pos-card mt-2">
                <CardBody className="pos-card-body p-0">
                  <FieldArray name="lignes">
                    {({ remove }) => (
                      <div className="pos-table-wrap">
                        <table className="table table-hover table-striped table-bordered mb-0 pos-table">
                          <thead className="pos-thead">
                            <tr>
                              <th>Article</th>
                              <th style={{ width: 140 }} className="text-center">Qté</th>
                              <th style={{ width: 160 }} className="text-center">Unité</th>
                              <th style={{ width: 180 }} className="text-end">PU TTC</th>
                              <th style={{ width: 160 }} className="text-end">Total</th>
                              <th style={{ width: 90 }} className="text-center">Actions</th>
                            </tr>
                          </thead>

                          <tbody>
                            {values.lignes.length === 0 ? (
                              <tr>
                                <td colSpan="6" className="text-center py-4">
                                  <div className="text-muted">{t("customerReturns.form.noItems")}</div>
                                </td>
                              </tr>
                            ) : (
                              values.lignes.map((line, index) => (
                                <tr key={index}>
                                  <td className="align-middle">
                                    <div className="fw-bold">{line.articleLibelle || "Article"}</div>
                                    {line.articleCode ? (
                                      <div className="text-muted small">{line.articleCode}</div>
                                    ) : null}
                                  </td>

                                  <td className="align-middle text-center">
                                    <Input
                                      value={line.quantiteRetournee ?? 1}
                                      readOnly
                                      className="form-control pos-mini-input text-center"
                                      onClick={() => {
                                        setNumberField({ type: "QTE", index });
                                        setNumberKeypadValue(String(line.quantiteRetournee ?? 1));
                                        setNumberKeypadOpen(true);
                                      }}
                                    />
                                  </td>

                                  <td className="align-middle text-center">
                                    {unites?.find((u) => u.code === line.unite)?.libelle ||
                                      line.unite ||
                                      "-"}
                                  </td>

                                  <td className="align-middle text-end">
                                    <InputGroup>
                                      <Input
                                        value={line.prixUnitaireTTC ?? 0}
                                        readOnly
                                        className="form-control pos-mini-input text-end"
                                        onClick={() => {
                                          setNumberField({ type: "PU", index });
                                          setNumberKeypadValue(String(line.prixUnitaireTTC ?? 0));
                                          setNumberKeypadOpen(true);
                                        }}
                                      />
                                    </InputGroup>
                                  </td>

                                  <td className="align-middle text-end fw-bold">
                                    {formatPrice(line.montantTTC || 0)}
                                  </td>

                                  <td className="align-middle text-center">
                                    <Button
                                      color="danger"
                                      size="sm"
                                      className="pos-icon-btn"
                                      onClick={() => remove(index)}
                                      disabled={!canBeUpdated}
                                    >
                                      <FaTrash />
                                    </Button>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </FieldArray>

                  {errors.lignes && typeof errors.lignes === "string" ? (
                    <div className="text-danger mt-2 px-3 pb-3 fw-bold">
                      {errors.lignes}
                    </div>
                  ) : null}
                </CardBody>
              </Card>

              {/* ===== BOTTOM STICKY BAR ===== */}
              <div className="pos-bottom-bar">
                <div className="pos-bottom-left">
                  <Badge color="dark" pill className="pos-pill">
                    Total TTC:{" "}
                    <span className="ms-1">
                      {formatPrice(totals.montantTTC)}
                    </span>
                  </Badge>
                </div>

                <div className="pos-bottom-right">
                  <Button
                    color="secondary"
                    className="pos-toolbar-btn"
                    type="button"
                    onClick={() => navigate(RETOUR_PAGE)}
                  >
                    {t("common.cancel")}
                  </Button>
                  <Button
                    color="primary"
                    className="pos-toolbar-btn-primary"
                    type="submit"
                    disabled={isSubmitting || !canSave}
                  >
                    {isSubmitting ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        {t("common.loading")}
                      </>
                    ) : (
                      t("common.save")
                    )}
                  </Button>
                </div>
              </div>

              {/* ========================= CLIENT MODAL ========================= */}
              <Modal isOpen={clientModal} toggle={toggleClientModal} size="lg">
                <ModalHeader toggle={toggleClientModal}>
                  <div className="d-flex align-items-center gap-2">
                    <FaUser />
                    <span>Choisir un client</span>
                  </div>
                </ModalHeader>

                <ModalBody>
                  <div className="pos-modal-search">
                    <Input
                      type="text"
                      value={clientSearch}
                      onChange={(e) => setClientSearch(e.target.value)}
                      className="pos-input"
                      placeholder="Rechercher (Nom / Entreprise / Code)"
                      autoFocus
                    />
                    {clientSearch ? (
                      <Button
                        type="button"
                        color="light"
                        className="pos-side-btn"
                        onClick={() => setClientSearch("")}
                      >
                        <FaTimes />
                      </Button>
                    ) : null}
                  </div>

                  <div className="pos-modal-table-wrap mt-3">
                    <Table hover bordered className="align-middle mb-0 pos-table">
                      <thead className="pos-thead">
                        <tr>
                          <th style={{ width: 120 }}>Code</th>
                          <th>Nom / Entreprise</th>
                          <th style={{ width: 120 }} className="text-center">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {clientList.length === 0 ? (
                          <tr>
                            <td colSpan="3" className="text-center py-4">
                              <div className="text-muted">Aucun client</div>
                            </td>
                          </tr>
                        ) : (
                          clientList.map((c) => (
                            <tr key={c.id} style={{ cursor: "pointer" }}>
                              <td className="fw-bold">{c.code || "-"}</td>
                              <td>
                                <div className="fw-bold">{c.label}</div>
                              </td>
                              <td className="text-center">
                                <Button
                                  type="button"
                                  size="sm"
                                  color="primary"
                                  onClick={() => {
                                    setFieldValue("clientId", c.id);
                                    toggleClientModal();
                                  }}
                                >
                                  Choisir
                                </Button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                </ModalBody>

                <ModalFooter>
                  <Button color="secondary" type="button" onClick={toggleClientModal}>
                    Fermer
                  </Button>
                </ModalFooter>
              </Modal>

              {/* ========================= PRODUCT MODAL ========================= */}
              <Modal isOpen={chooseProductModal} toggle={toggleChooseProductModal} size="xl">
                <ModalHeader toggle={toggleChooseProductModal}>
                  {t("product.chooseProduct")}
                </ModalHeader>

                <ModalBody>
                  <Row className="g-2 align-items-end">
                    <Col md={12}>
                      <Label className="pos-label">{t("product.searchPlaceholder")}</Label>
                      <div className="pos-modal-search">
                        <Input
                          type="text"
                          value={searchValue}
                          readOnly
                          inputMode="none"
                          className="pos-input pos-nokeyboard"
                          placeholder={t("product.searchPlaceholder")}
                          onClick={() => setSearchKeypadOpen(true)}
                        />
                        {modalSearchTerm ? (
                          <Button
                            type="button"
                            color="light"
                            className="pos-side-btn"
                            onClick={() => {
                              setModalSearchTerm("");
                              setCurrentPage(1);
                              dispatch(productActions.findAll({ search: "", page: 0, size: 10 }));
                            }}
                          >
                            <FaTimes />
                          </Button>
                        ) : null}
                        <Button
                          type="button"
                          color="primary"
                          className="pos-side-btn"
                          onClick={() =>
                            dispatch(productActions.findAll({ search: modalSearchTerm, page: 0, size: 10 }))
                          }
                        >
                          <FaSearch />
                        </Button>
                      </div>
                    </Col>
                  </Row>

                  <div className="pos-modal-table-wrap mt-3">
                    <Table hover bordered className="align-middle mb-0 pos-table">
                      <thead className="pos-thead">
                        <tr>
                          <th>
                            <FaBarcode className="me-2 text-primary" size={12} />
                            {t("product.code")}
                          </th>
                          <th>
                            <FaBox className="me-2 text-primary" size={12} />
                            {t("product.description")}
                          </th>
                          <th className="text-end">
                            <FaTag className="me-2 text-primary" size={12} />
                            {t("product.salePrice")}
                          </th>
                          <th className="text-center">
                            <FaBoxes className="me-2 text-primary" size={12} />
                            {t("product.stockPhysique")}
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {!products || products.length === 0 ? (
                          <tr>
                            <td colSpan="4" className="text-center py-4">
                              <div className="text-muted">
                                {isSearching ? t("common.loading") : t("product.noProducts")}
                              </div>
                            </td>
                          </tr>
                        ) : (
                          products.map((p) => {
                            const already = (values.lignes || []).some(
                              (l) => l.articleId === p.id || l.article === p.id
                            );

                            return (
                              <tr
                                key={p.id}
                                style={{
                                  cursor: canBeUpdated ? "pointer" : "not-allowed",
                                  backgroundColor: already ? "#f8fafc" : "transparent",
                                }}
                                className={already ? "text-muted" : ""}
                                onClick={() => {
                                  if (!canBeUpdated) return;

                                  if (already) {
                                    toast.warning(t("customerReturns.messages.itemAlreadyAdded"));
                                    return;
                                  }

                                  const newLine = recalcLineAmounts({
                                    article: p.id,
                                    articleId: p.id,
                                    articleLibelle: p.libelle,
                                    articleCode: p.code,
                                    quantiteRetournee: 1,
                                    quantiteOriginale: 1,
                                    prixUnitaireHT:
                                      p.prixVenteHT ||
                                      Number(p.prixVenteTTC1 || 0) / (1 + Number(p.tauxTVA || 0.2)),
                                    prixUnitaireTTC: p.prixVenteTTC1,
                                    tauxTVA: p.tauxTVA || 0.2,
                                    unite: p.unite,
                                  });

                                  setFieldValue("lignes", [...values.lignes, newLine]);
                                  toggleChooseProductModal();
                                }}
                              >
                                <td className="fw-bold">{p.code}</td>
                                <td>
                                  {p.libelle?.length > 40 ? p.libelle.substring(0, 40) + "..." : p.libelle}
                                  {already ? (
                                    <span className="ms-2 badge bg-light text-dark">
                                      Déjà ajouté
                                    </span>
                                  ) : null}
                                </td>
                                <td className="text-end fw-bold">{formatPrice(p.prixVenteTTC1)}</td>
                                <td className="text-center">{p.stockPhysique}</td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </Table>
                  </div>

                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <small className="text-muted">
                      {t("product.showing", {
                        start: (currentPage - 1) * (pageSize || 10) + 1,
                        end: Math.min(currentPage * (pageSize || 10), totalElements || 0),
                        total: totalElements || 0,
                        currentPage,
                        totalPages: totalPages || 1,
                      })}
                    </small>

                    <div className="d-flex gap-2">
                      <Button
                        type="button"
                        color="secondary"
                        disabled={currentPage <= 1}
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      >
                        ◀
                      </Button>
                      <Button
                        type="button"
                        color="secondary"
                        disabled={totalPages ? currentPage >= totalPages : true}
                        onClick={() => setCurrentPage((p) => p + 1)}
                      >
                        ▶
                      </Button>
                    </div>
                  </div>
                </ModalBody>

                <ModalFooter>
                  <Button type="button" color="secondary" onClick={toggleChooseProductModal}>
                    {t("common.cancel")}
                  </Button>
                </ModalFooter>
              </Modal>

              <POSKeypadModal
                isOpen={textKeypadOpen}
                toggle={() => setTextKeypadOpen(false)}
                value={textKeypadValue}
                onChange={setTextKeypadValue}
                onSearch={() => {
                  setFieldValue(textFieldName, textKeypadValue);
                  setTextKeypadOpen(false);
                }}
                title="Saisir Motif"
              />

              <POSKeypadModal
                isOpen={searchKeypadOpen}
                toggle={() => setSearchKeypadOpen(false)}
                value={searchValue}
                onChange={setSearchValue}
                onSearch={() => {
                  triggerProductSearch(searchValue);
                  setSearchKeypadOpen(false);
                }}
                title="Recherche Produit"
              />

              <POSNumericKeypadModal
                isOpen={numberKeypadOpen}
                toggle={() => setNumberKeypadOpen(false)}
                value={numberKeypadValue}
                onChange={setNumberKeypadValue}
                onConfirm={(val) => {
                  const num = Number(val) || 0;

                  if (numberField?.type === "QTE") {
                    const updatedLine = recalcLineAmounts({
                      ...values.lignes[numberField.index],
                      quantiteRetournee: num,
                    });
                    setFieldValue(`lignes.${numberField.index}`, updatedLine);
                  }

                  if (numberField?.type === "PU") {
                    const updatedLine = recalcLineAmounts({
                      ...values.lignes[numberField.index],
                      prixUnitaireTTC: num,
                    });
                    setFieldValue(`lignes.${numberField.index}`, updatedLine);
                  }

                  setNumberKeypadOpen(false);
                }}
                title="Saisie numérique"
              />
            </Form>
          );
        }}

      </Formik>



    </Container>
  );
};

export default CustomerReturnForm;
